<template>
  <div
    :class="[
      'category-wrapper',
      isExpanded ? 'category-expanded' : '',
      props.value === data.a_id ? 'category-active' : '',
    ]"
  >
    <div
      @click="handleRowClick"
      :class="[
        'category-header',
        hasHeaderBorderBottom ? 'category-header-border-bottom' : '',
        selectable.groups ? 'category-selectable' : '',
      ]"
    >
      <div class="category-header__selection-data">
        <div class="category-header__data">
          <CRadioIcon v-if="selectable.groups" :active="props.value === data.a_id" />
          <div class="category-header__title-data">
            <p class="category-section-title">{{ data.description }}</p>
          </div>
        </div>
        <div v-if="foundItem && !isExpanded" class="category-header__selected-item-data">
          <CIcon name="check-icon" />
          <p>{{ $t("driveBooking.selected") }} {{ foundItem?.description }}</p>
        </div>
      </div>
      <div class="category-header__arrow-container">
        <div class="category-header__count-wrapper">
          <p class="category-header__count">{{ data.availability }}</p>
        </div>
        <div
          @click="handleExpand"
          v-if="shouldShowCollapseArrow"
          class="category-header__arrow-wrapper"
        >
          <CDirectionIcon :direction="isExpanded ? 'up' : 'down'" />
        </div>
      </div>
    </div>
    <transition
      @enter="onEnter"
      @after-enter="onAfterEnter"
      @leave="onLeave"
      @after-leave="onAnimationEnd"
    >
      <div :class="['category-content']" ref="contentRef" v-show="isExpanded">
        <div ref="innerRef" class="category-inner">
          <div v-if="selectable.classes || visibility.classes">
            <CCarSubcategory
              :selectable="selectable"
              @change="onValueChange"
              v-for="item in data.classes"
              :visibility="visibility"
              :data="item"
              :value="props.value"
            />
          </div>
          <div v-else-if="selectable.assets || visibility.assets">
            <CCarSelectionOption
              :selectable="selectable"
              v-for="item in data.assets"
              @change="handleAssetSelection"
              :key="item.a_id"
              :checked="props.value === item.a_id"
              :valueId="item.a_id"
              :title="item.brand.name"
              :details="item.description"
              :licensePlate="item.registrationNumber"
              :logo="item.brand.logoUrl"
              :value="props.value"
            />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref, watchEffect } from 'vue';
  import CRadioIcon from '@/shared/components/form/CRadioIcon.vue';
  import CCarSelectionOption from './CCarSelectionOption.vue';
  import CDirectionIcon from '@/shared/components/CDirectionIcon.vue';
  import CCarSubcategory from './CCarSubcategory.vue';
  import { useExpandableContent } from '@/shared/utils/useExpandableContent';
  import { DriveAsset,
           DriveClass,
           DriveGroup } from '../utils/driveReservationCreationMapper';
  import CIcon from '@/shared/components/CIcon.vue';

  const props = defineProps<{
    data: DriveGroup;
    visibility: {
      groups: boolean;
      classes: boolean;
      assets: boolean;
    };
    selectable: {
      groups: boolean;
      classes: boolean;
      assets: boolean;
    };
    value: string;
    onChange: (v: string) => void;
  }>();

  const shouldExpandInitially = ref(true);

  const foundItem = ref<DriveClass | DriveAsset | null>(null);

  const hasHeaderBorderBottom = ref(false);
  const {
    contentRef,
    innerRef,
    isExpanded,
    onEnter,
    onAfterEnter,
    onLeave,
    toggleExpanded,
  } = useExpandableContent();

  onMounted(() => {
    if (foundItem.value && shouldExpandInitially.value) {
      toggleExpanded(null);
      hasHeaderBorderBottom.value = true;
    }
  });

  const emit = defineEmits<{
    (e: 'change', v: string): void;
  }>();

  const handleAssetSelection = (value: string) => {
    if (props.selectable.assets) {
      emit('change', value);
      shouldExpandInitially.value = false;
    }
  };

  const handleRowClick = (e: MouseEvent) => {
    handleGroupSelection();

    if (shouldShowCollapseArrow.value) {
      handleExpand(e);
      shouldExpandInitially.value = false;
    }
  };

  function findItemById(targetId: string) {
    const group = props.data;

    if (group.classes && group.classes.length > 0) {
      for (const cls of group.classes) {
        if (cls.a_id === targetId) {
          return cls;
        }

        if (cls.assets && cls.assets.length > 0) {
          const foundAsset = cls.assets.find((asset) => asset.a_id === targetId);
          if (foundAsset) {
            return foundAsset;
          }
        }
      }
    }

    if (group.assets && group.assets.length > 0) {
      const foundAsset = group.assets.find((asset) => asset.a_id === targetId);
      if (foundAsset) {
        return foundAsset;
      }
    }

    return null;
  }

  watchEffect(() => {
    if (props.value) {
      foundItem.value = findItemById(props.value);
    }
  });

  const onValueChange = (value: string) => {
    emit('change', value);
  };

  const shouldShowCollapseArrow = computed(
    () =>
      (props.data.classes && props.data.classes.length > 0)
      || (props.data.assets && props.data.assets.length > 0),
  );

  function handleGroupSelection() {
    if (props.selectable.groups) {
      emit('change', `${props.data.a_id}`);
    }
  }

  function handleExpand(e: MouseEvent) {
    shouldExpandInitially.value = false;
    toggleExpanded(e);

    if (isExpanded.value) {
      hasHeaderBorderBottom.value = true;
    }
  }

  function onAnimationEnd() {
    hasHeaderBorderBottom.value = false;
  }

  watchEffect(() => {
    if (
      props.value === props.data.a_id
      && ((props.data.classes && props.data.classes.length > 0)
        || (props.data.assets && props.data.assets.length > 0))
    ) {
      isExpanded.value = true;
      hasHeaderBorderBottom.value = true;
    }
  });
</script>

<style lang="scss">
.category-wrapper {
  border: 1px solid $neutral-300;
  border-radius: 5px;
  background-color: $neutral-0;
}

.category-header__arrow-wrapper {
  padding-inline: 5px;
  color: #abb5ba;
  cursor: pointer;
}

.category-header__arrow-wrapper:hover {
  background-color: #2225261a;
  border-radius: 3px;
}

.category-header {
  display: flex;
  justify-content: space-between;
  padding-right: 16px;
  height: 56px;
}

.category-header__selection-data {
  display: flex;
  gap: 20px;
  align-items: center;
}

.category-selectable {
  cursor: pointer;
}

.category-header-border-bottom {
  border-bottom: 1px solid $neutral-300;
}

.category-header__data {
  display: flex;
  gap: 10px;
  padding-left: 15px;
  align-items: center;
}

.category-header__title-data {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.category-section-code {
  font-size: 12px;
  line-height: 10px;
}

  .category-inner{
    padding-top:5px;
    padding-left: 20px;
    padding-bottom: 5px;
  }

.category-content {
  display: flex;
  flex-direction: column;
  gap: 7px;
  overflow: hidden;
  transition: height 400ms;
  transition-timing-function: cubic-bezier(0.22, 0.98, 0.3, 1);
}

.category-content.active {
  height: auto;
}

.category-section-title {
  font-weight: 500;
  color: #212528;
  line-height: 10px;
}

.category-expanded {
  box-shadow: 0px 0px 2px 0px #0000004d, 0px 4px 8px 0px #0000001a;
}

.category-header__arrow-container {
  display: flex;
  gap: 5px;
  margin-block: auto;
}

.category-header__count-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px 7px;
  border-radius: 12px;
  background-color: #cdd2d6;
}

.category-header__count {
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  color: #212528;
}

.category-active > .category-header {
  background-color: #e0f4ff;
  outline: 2px solid #64b8e6;
  border-radius: 5px;
}

.category-header__selected-item-data {
  display: flex;
  gap: 9px;
  align-items: center;
  line-height: 1px;
}

.category-inner > div > .option-wrapper {
  margin-bottom: 8px;
  margin-right: 5px;
}

.category-inner > div > .option-wrapper:last-child {
  margin-bottom: 3px;
}

.category-inner > div > .option-wrapper:first-child {
  margin-top: 3px;
}
</style>
